import { toast } from 'react-toastify'

import { handleError } from './apiUtils'
import { getConfigurationAsync, xKMSClient } from './httpMethods'
import {
  EndorsementKeysApi,
  GenericKeyResponseDto,
  GetKeysRequestDto,
  SearchKeysResponseDtoEndorsementKeyEntity,
} from './kmsapiclient'

/**
 * Uploads an endorsement key asynchronously.
 *
 * @param file - The file path of the endorsement key to upload.
 * @param keyName - The name of the endorsement key.
 * @param aadRevocation - The AAD revocation status.
 * @returns A promise that resolves to a `GenericKeyResponseDto` object.
 */
export async function uploadEndorsementKeyAsync(
  file: string,
  keyName: string,
  aadRevocation: string,
): Promise<GenericKeyResponseDto> {
  try {
    const endorsementKeysApi = new EndorsementKeysApi(getConfigurationAsync())
    const reponse = (
      await endorsementKeysApi.endorsementKeyUpload(
        file,
        keyName,
        aadRevocation,
        xKMSClient,
      )
    ).data
    return reponse
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Searches for endorsement keys based on the provided request.
 *
 * @param request - The request object containing search criteria.
 * @returns A promise that resolves to the search response.
 */
export async function searchEndorsementKeysAsync(
  request: GetKeysRequestDto,
): Promise<SearchKeysResponseDtoEndorsementKeyEntity> {
  try {
    const endorsementKeysApi = new EndorsementKeysApi(getConfigurationAsync())
    const response = (
      await endorsementKeysApi.endorsementKeysSearch(xKMSClient, request)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Retrieves an endorsement key based on the provided request.
 *
 * @param keyId - The ID of the endorsement key to retrieve.
 * @returns A promise that resolves to the endorsement key response.
 */
export async function updateEndorsementKeyAsync(
  keyId: string,
  key,
): Promise<GenericKeyResponseDto> {
  const handleError = (res) => {
    let errorMessage = res.message
    const errorCode =
      res.response && res.response.data && res.response.data.errorCode

    if (errorCode) {
      if (
        errorCode == 420 &&
        confirm(
          'This endorsement key has been modified by another session.\n\nReload current session?\n\nThe most recent changes will be discarded.',
        )
      ) {
        location.reload()
        return
      } else {
        errorMessage = `Please contact your administrator. (Error Code: ${errorCode}, Status Code: ${res.response.status})`
      }
    }
    toast.error(errorMessage)
  }

  try {
    const endorsementKeysApi = new EndorsementKeysApi(getConfigurationAsync())
    const response = (
      await endorsementKeysApi.endorsementKeyUpdate(keyId, xKMSClient, key)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Retrieves an endorsement key based on the provided request.
 *
 * @param keyId - The ID of the endorsement key to retrieve.
 * @returns A promise that resolves to the endorsement key response.
 */
export async function removeEndorsementKey(keyId: string): Promise<string> {
  try {
    const endorsementKeysApi = new EndorsementKeysApi(getConfigurationAsync())
    const response = (
      await endorsementKeysApi.endorsementKeyDelete(keyId, xKMSClient)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}
